// --- Logic --------------------------------------------------------------------------------------
export default {
    /** Already ends in `/clients` — pass only what comes after it to `useFetch`. */
    url: import.meta.env.VITE_API_URL,

    /** Mirrors the backend's `LANGUAGES`, in canonical order. */
    languages: ["ar", "en"] as const,
    /** Which of them render right to left. Read this instead of testing for `"ar"` by hand. */
    rtlLanguages: ["ar"] as const,

    /**
     * Each language in its own words, never translated: the tag on a bilingual field says which box
     * you are typing in, and "Arabic" in an English UI is the wrong answer to that — the person
     * typing Arabic is looking for عربي.
     */
    languageNames: { ar: "عربي", en: "English" } as Record<string, string>,

    /** Mirrors the backend's `CLIENT_SUPER_MODELS` — the sections a member never sees. */
    superModels: ["trash", "team", "subscription", "usage", "settings"] as string[],

    /** Mirrors the backend's `MIN_PASSWORD_LENGTH`. */
    minPasswordLength: 8,

    /** Mirrors the backend's `HEX_COLOR_REGEX` — the brand colour the catalog is painted with. */
    hexColorPattern: /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/,
    /** Mirrors the backend's `DEFAULT_BRAND_COLOR`: what a catalog uses before a company picks one. */
    defaultBrandColor: "#C7880B",

    /** Tabs the phone bar shows before the rest move behind "more" — five targets is the practical limit. */
    bottomBarTabs: 4,

    /** Mirrors the backend's `THUMB`. Thumbnails are drawn here now — the server never sees the bytes. */
    thumbnail: { maxSize: 400, quality: 0.7, mimeType: "image/webp" },

    /** Files uploading at once. Three keeps a bar moving per file without starving the line. */
    uploadConcurrency: 3,

    // --- Properties, mirrored from the backend's `constants.ts` ---------------------------------
    unitTypes: ["apartment", "villa", "duplex", "townhouse", "floor", "studio", "office", "shop", "warehouse", "garage", "land", "other"] as const,
    projectStages: ["planning", "underConstruction", "ready"] as const,
    unitUsages: ["residential", "commercial"] as const,
    unitOffers: ["sale", "rent"] as const,
    unitStatuses: ["available", "reserved", "sold", "rented"] as const,
    acSystems: ["central", "split", "window", "concealed", "other", "none"] as const,
    unitFacades: ["north", "south", "east", "west"] as const,
    warrantyTypes: ["structure", "waterproofing", "plumbing", "electrical", "hvac", "elevators", "finishes", "appliances", "other"] as const,
    /**
     * Derived, never stored — the filter sends the word and the API turns it back into a date range.
     * `notStarted` is the one the API never sees: it is a unit's template, which has no record yet.
     */
    warrantyStates: ["notStarted", "active", "expiring", "expired"] as const,
    /** Mirrors the backend's `WARRANTY_EXPIRING_DAYS`, so a card and its filter agree on "soon". */
    warrantyExpiringDays: 30,

    // --- CRM, mirrored from the backend's `constants.ts` ----------------------------------------
    customerStatuses: ["lead", "client"] as const,
    leadStages: ["new", "contacted", "qualified", "offer", "won", "lost"] as const,
    /** The stages a deal passes through. `won` and `lost` are ends, not steps, so they are not here. */
    leadWorkingStages: ["new", "contacted", "qualified", "offer"] as const,
    /** Nobody types these — a reservation or a contract writes them. */
    leadSystemStages: ["won"] as const,
    leadTerminalStages: ["won", "lost"] as const,
    leadSources: [
        "catalog",
        "manual",
        "call",
        "whatsapp",
        "walkIn",
        "referral",
        "exhibition",
        "snapchat",
        "instagram",
        "tiktok",
        "facebook",
        "x",
        "google",
        "portal",
        "other",
    ] as const,
    leadLostReasons: ["price", "timing", "boughtElsewhere", "unreachable", "notSerious", "other"] as const,
    leadCloseReasons: ["duplicate", "superseded", "mistake", "other"] as const,
    leadFinancing: ["cash", "mortgage"] as const,
    /** Where one recorded interest stands, inside a lead that has only one stage to its name. */
    interestStatuses: ["active", "hesitant", "stopped"] as const,
    interactionChannels: ["call", "whatsapp", "sms", "email", "visit", "catalog", "other"] as const,
    /** `guarantee` is the after-the-sale one: a warranty call or visit. */
    interactionCategories: ["marketing", "response", "followUp", "guarantee", "other"] as const,
    interactionDirections: ["inbound", "outbound"] as const,
    /**
     * The outcome. `null` is a third state and means "not yet" — see the backend's `Interaction`.
     * The form writes it as a tick rather than a picker: there are two values and the question is
     * "did they answer". The list stays because `TyInteractionStatus` is built from it.
     */
    interactionStatuses: ["completed", "failed"] as const,

    /**
     * Mirrors the backend's `FILE_SLOTS` — **what a file is to its record**, as opposed to merely
     * being filed under it. `null` is the drive's own: a loose document in a record's folder.
     */
    fileSlots: ["picture", "gallery", "logo", "paper", "receipt", "identity"] as const,

    /**
     * Every date in this product is a Saudi date. Pinned once so a booking made in Riyadh reads
     * back as the same hour on a laptop anywhere else — see `UI/inputs/DateTimeInput`.
     */
    timeZone: "Asia/Riyadh",

    /** Mirrors the backend's `UNIT_BULK_MAX`. */
    unitBulkMax: 200,

    /**
     * The unit's grouped attributes, in the order a form shows them. The **only** list of these keys in
     * the app: every group renders from here, so adding an attribute is one line in the model and one
     * here, never a hand-written field somewhere in a screen.
     */
    unitGroups: {
        measures: ["landArea", "builtArea", "streetWidth"],
        counts: [
            "bedrooms",
            "masterBedrooms",
            "livingRooms",
            "receptions",
            "bathrooms",
            "kitchens",
            "maidRooms",
            "driverRooms",
            "storageRooms",
            "parkings",
            "balconies",
            "pools",
            "elevators",
            "floors",
        ],
        flags: [
            "hasBasement",
            "hasAnnex",
            "hasRoof",
            "hasGarden",
            "hasPrivateEntrance",
            "hasDriveway",
            "hasLaundryRoom",
            "hasInternalStairs",
            "hasFittedKitchen",
            "isFurnished",
            "hasFiberOptic",
            "hasWater",
            "hasElectricity",
            "hasSewage",
            "isNew",
        ],
    } as const,

    /**
     * How many rows a list asks for by default. A person can change it from any paginator and the
     * choice is remembered — `hooks/usePageSize` owns that; this is only where it starts.
     * The ceiling is the backend's own cap on `limit`, repeated here so the input cannot ask for
     * a page the API will silently refuse to give.
     */
    pageSize: 24,
    minPageSize: 5,
    maxPageSize: 100,

    /** The drive's four buckets, mirrored from the backend's `FILE_CATEGORIES`. */
    fileCategories: ["image", "video", "document", "model", "archive"] as const,

    // --- Billing, mirrored from the backend's `constants.ts` ------------------------------------
    /**
     * The order the two plans are shown in, cheapest first. The prices themselves are **not** here:
     * they are sold, so they come from `/subscription/config` and a stale copy in the dashboard
     * would quote a company a figure we no longer charge.
     */
    subscriptionPeriods: ["quarterly", "annually"] as const,
    subscriptionStatuses: ["pending", "active", "coming", "grace", "ended", "cancelled"] as const,
    /** Mirrors the backend's `GENDERS`. Blank is a third answer and the one most people leave. */
    genders: ["male", "female"] as const,

    // --- Support, mirrored from the backend's `constants.ts` ------------------------------------
    /** Where a request for help stands. The client never sets one — only reads it. */
    supportStatuses: ["pending", "contacted", "solved"] as const,
    supportSubjectMax: 120,
    supportMessageMax: 2000,

    /** The logo set in `public/logo`. */
    logo: {
        icon: "/logo/lozicore-icon.svg",
        round: "/logo/lozicore-round.svg",
        square: "/logo/lozicore-square.svg",
        circle: "/logo/lozicore-circle.svg",
    },
};
